import Box from '@mui/material/Box';
import {
  DataGridPro,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridPinnedColumnPosition,
  GridRowClassNameParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { forwardRef, ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react';
import * as HOOKS from '@siloreal:shared:hooks';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const AUTO_ROW_HEIGHT = () => 64 as const;
const INITIAL_LOADING_HEIGHT = 256;

export type RowStyleVariant =
  | 'danger'
  | 'major'
  | 'minor'
  | 'spotlight'
  | 'safe'
  | 'warning'
  | 'basic'
  | 'none';
export type RowStyleVariantResolver = (row: GridValidRowModel) => RowStyleVariant | undefined;

const ROW_CLASSNAME_BY_VARIANT: Record<RowStyleVariant, string> = {
  danger: 'sr-table-row-danger',
  major: 'sr-table-row-major',
  minor: 'sr-table-row-minor',
  spotlight: 'sr-table-row-spotlight',
  safe: 'sr-table-row-safe',
  warning: 'sr-table-row-warning',
  basic: 'sr-table-row-basic',
  none: 'sr-table-row-none',
};

type RowStyleConfig = {
  backgroundStart: string;
  backgroundMid: string;
  hoverBackground: string;
};

type StyledRowStyleVariant = Exclude<RowStyleVariant, 'none'>;

const ROW_STYLE_BY_VARIANT: Record<StyledRowStyleVariant, RowStyleConfig> = {
  danger: {
    backgroundStart: 'rgba(251, 209, 209, 0.95)',
    backgroundMid: 'rgba(251, 209, 209, 0.58)',
    hoverBackground: 'rgba(251, 209, 209, 0.4)',
  },
  major: {
    backgroundStart: 'rgba(168, 92, 227, 0.27)',
    backgroundMid: 'rgba(168, 92, 227, 0.14)',
    hoverBackground: 'rgba(168, 92, 227, 0.1)',
  },
  minor: {
    backgroundStart: 'rgba(73, 69, 79, 0.82)',
    backgroundMid: 'rgba(73, 69, 79, 0.46)',
    hoverBackground: 'rgba(73, 69, 79, 0.08)',
  },
  spotlight: {
    backgroundStart: 'rgba(239, 254, 236, 0.95)',
    backgroundMid: 'rgba(239, 254, 236, 0.58)',
    hoverBackground: 'rgba(239, 254, 236, 0.75)',
  },
  safe: {
    backgroundStart: 'rgba(225, 238, 204, 0.95)',
    backgroundMid: 'rgba(225, 238, 204, 0.58)',
    hoverBackground: 'rgba(225, 238, 204, 0.5)',
  },
  warning: {
    backgroundStart: 'rgba(255, 248, 225, 0.95)',
    backgroundMid: 'rgba(255, 248, 225, 0.58)',
    hoverBackground: 'rgba(255, 248, 225, 0.75)',
  },
  basic: {
    backgroundStart: 'rgba(243, 242, 247, 0.95)',
    backgroundMid: 'rgba(243, 242, 247, 0.62)',
    hoverBackground: 'rgba(243, 242, 247, 0.9)',
  },
};

const ROW_STYLE_SX = Object.entries(ROW_STYLE_BY_VARIANT).reduce(
  (acc, [variant, style]) => {
    const className = ROW_CLASSNAME_BY_VARIANT[variant as StyledRowStyleVariant];
    const gradient = `linear-gradient(90deg, ${style.backgroundStart} 0%, ${style.backgroundMid} 4%, rgba(255, 255, 255, 0) 14%)`;
    const hoverBackground = `linear-gradient(${style.hoverBackground}, ${style.hoverBackground})`;

    acc[`& .${className}`] = {
      backgroundImage: gradient,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'auto 88%',
      backgroundPosition: 'center',
      borderRadius: '14px',
    };
    acc[`& .${className}:hover`] = {
      backgroundColor: 'transparent',
      backgroundImage: hoverBackground,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'auto 88%',
      backgroundPosition: 'center',
      borderRadius: '14px',
    };
    acc[
      `& .${className}:hover .MuiDataGrid-cell--pinnedLeft, & .${className}:hover .MuiDataGrid-cell--pinnedRight`
    ] = {
      backgroundColor: '#ffffff',
      backgroundImage: hoverBackground,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'auto 88%',
      backgroundPosition: 'center',
    };

    return acc;
  },
  {} as Record<string, object>
);

const GRID_INTERACTION_SX = {
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none !important',
  },
};

const PINNED_COLUMN_SHADOW_WIDTH = 8;
const PINNED_COLUMN_SHADOW_COLOR = 'rgba(73, 69, 79, 0.22)';
const PINNED_COLUMN_SHADOW_TRANSPARENT = 'rgba(73, 69, 79, 0)';

const PINNED_COLUMN_SX = {
  '& .MuiDataGrid-main': {
    position: 'relative',
  },
  '& .MuiDataGrid-main:has(.MuiDataGrid-columnHeader--pinnedLeft)::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 'var(--DataGrid-leftPinnedWidth, 0px)',
    width: PINNED_COLUMN_SHADOW_WIDTH,
    zIndex: 50,
    pointerEvents: 'none',
    background: `linear-gradient(to right, ${PINNED_COLUMN_SHADOW_COLOR}, ${PINNED_COLUMN_SHADOW_TRANSPARENT})`,
  },
  '& .MuiDataGrid-main:has(.MuiDataGrid-columnHeader--pinnedRight)::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 'var(--DataGrid-rightPinnedWidth, 0px)',
    width: PINNED_COLUMN_SHADOW_WIDTH,
    zIndex: 50,
    pointerEvents: 'none',
    background: `linear-gradient(to left, ${PINNED_COLUMN_SHADOW_COLOR}, ${PINNED_COLUMN_SHADOW_TRANSPARENT})`,
  },
};

export type PinnedColumnPosition = `${GridPinnedColumnPosition}`;

export type PinnedColumns<TColumns extends readonly GridColDef[] = readonly GridColDef[]> = Partial<
  Record<TColumns[number]['field'], PinnedColumnPosition>
>;

const toGridPinnedColumnFields = (pinnedColumns?: PinnedColumns): GridPinnedColumnFields | undefined => {
  if (!pinnedColumns) return undefined;

  const pinnedColumnFields: GridPinnedColumnFields = {};

  Object.entries(pinnedColumns).forEach(([field, position]) => {
    if (!position) return;

    (pinnedColumnFields[position] ??= []).push(field);
  });

  return pinnedColumnFields;
};

const toIncludeSelectionModel = (model: GridRowSelectionModel, rows: GridRowsProp): GridRowSelectionModel => {
  if (model.type === 'include') return model;

  const includedIds = new Set<GridRowId>(
    rows.map((row) => row.id as GridRowId).filter((rowId) => !model.ids.has(rowId))
  );

  return { type: 'include', ids: includedIds };
};

export type Props = {
  rows: GridRowsProp;
  columns: readonly GridColDef[];
  height?: number | string;
  loading?: boolean;
  pageSizeOptions?: number[];
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  isRowSelectable?: (params: GridRowParams) => boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionChange?: (model: GridRowSelectionModel, details: GridCallbackDetails) => void;
  pinnedColumns?: PinnedColumns;
  getRowStyleVariant?: RowStyleVariantResolver;
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  onPaginationChange?: (model: GridPaginationModel, details: GridCallbackDetails<'pagination'>) => void;
  sortingModel?: GridSortModel;
  onSortingChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
  tid?: string;
};

export const Table: FR<HTMLDivElement, Props> = forwardRef((props: Props, ref) => {
  const {
    rows,
    columns,
    height,
    loading = false,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    checkboxSelection = false,
    disableRowSelectionOnClick = true,
    isRowSelectable,
    rowSelectionModel,
    onRowSelectionChange,
    pinnedColumns,
    getRowStyleVariant,
    rowCount = rows.length,
    paginationModel,
    onPaginationChange,
    sortingModel,
    onSortingChange,
    tid,
  } = props;

  const hasFixedHeight = height !== undefined;
  const isInitialLoading = loading && rows.length === 0;
  const initialLoadingHeight = isInitialLoading && !hasFixedHeight ? INITIAL_LOADING_HEIGHT : undefined;

  const handleGetRowClassName = getRowStyleVariant
    ? (params: GridRowClassNameParams) => {
        const variant = getRowStyleVariant(params.row);
        if (!variant || variant === 'none') return '';
        return ROW_CLASSNAME_BY_VARIANT[variant];
      }
    : undefined;

  const i18n = HOOKS.useLocale();
  const NO_ROWS_LABEL = i18n.t('temp.common.no_rows');
  const PAGINATION_ROWS_PER_PAGE_LABEL = i18n.t('temp.common.rows_per_page');

  return (
    <Box
      ref={ref}
      data-testid={tid}
      sx={{
        height: hasFixedHeight ? height : (initialLoadingHeight ?? 'auto'),
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <DataGridPro
        // [SECTION] Data
        rows={rows}
        columns={columns}
        loading={loading}
        slotProps={{
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'circular-progress' },
        }}
        // [SECTION] Pinning
        pinnedColumns={toGridPinnedColumnFields(pinnedColumns)}
        // [SECTION] Style
        getRowHeight={AUTO_ROW_HEIGHT}
        getRowClassName={handleGetRowClassName}
        sx={{
          ...ROW_STYLE_SX,
          ...GRID_INTERACTION_SX,
          ...PINNED_COLUMN_SX,
          border: 'none',
          height: hasFixedHeight || initialLoadingHeight !== undefined ? '100%' : 'auto',
        }}
        // [SECTION] Pagination
        pagination
        paginationModel={paginationModel}
        pageSizeOptions={pageSizeOptions}
        paginationMode="server"
        rowCount={rowCount}
        onPaginationModelChange={(model, details) => onPaginationChange?.(model, details)}
        // [SECTION] Sorting
        sortModel={sortingModel}
        sortingMode="server"
        onSortModelChange={(model, details) => onSortingChange?.(model, details)}
        // [SECTION] Selection
        checkboxSelection={checkboxSelection}
        checkboxSelectionVisibleOnly={checkboxSelection}
        keepNonExistentRowsSelected={checkboxSelection}
        isRowSelectable={isRowSelectable}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(model, details) =>
          onRowSelectionChange?.(toIncludeSelectionModel(model, rows), details)
        }
        // [SECTION] Disabling
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        // [SECTION] change default texts.
        localeText={{
          noRowsLabel: NO_ROWS_LABEL,
          paginationRowsPerPage: PAGINATION_ROWS_PER_PAGE_LABEL,
        }}
      />
    </Box>
  );
});
